import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Gift, Plus, Trash2, Eye, Palette, Settings2, QrCode, Copy,
  Loader2, Save, Check, Download, CreditCard, TrendingUp, Users
} from 'lucide-react';

const FONT_OPTIONS = [
  { v: 'inter', l: 'Inter' }, { v: 'poppins', l: 'Poppins' },
  { v: 'playfair', l: 'Playfair Display' }, { v: 'georgia', l: 'Georgia' },
];

const FONT_MAP = {
  inter: "'Inter',sans-serif", poppins: "'Poppins',sans-serif",
  playfair: "'Playfair Display',serif", georgia: "Georgia,serif",
};

const PRESET_AMOUNTS = [25, 50, 75, 100, 150, 200];

function GiftCardVisual({ design, restaurant, card }) {
  const font = FONT_MAP[design.fontFamily] || "'Inter',sans-serif";
  const bg = design.gradient
    ? `linear-gradient(${design.gradientDir || '135deg'}, ${design.primaryColor}, ${design.secondaryColor})`
    : design.backgroundColor;
  return (
    <div style={{ fontFamily: font, background: bg, borderRadius: design.borderRadius || 16, padding: '28px 32px', minHeight: 180, position: 'relative', overflow: 'hidden', color: design.textColor || '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', userSelect: 'none' }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', right: -40, top: -40, width: 160, height: 160, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)' }} />
      <div style={{ position: 'absolute', right: 20, bottom: -60, width: 140, height: 140, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.06)' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Logo/name area */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', opacity: 0.75, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Tarjeta Regalo</div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>{restaurant?.name || 'OkReserva Restaurant'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, opacity: 0.75, letterSpacing: '0.1em', marginBottom: 2 }}>VALOR</div>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.02em' }}>€{card?.amount || 50}</div>
          </div>
        </div>

        {/* Tagline */}
        <div style={{ fontSize: 11, opacity: 0.8, fontStyle: 'italic', marginBottom: 20, lineHeight: 1.5, maxWidth: '60%' }}>
          {design.tagline || 'El regalo perfecto para los amantes de la buena mesa'}
        </div>

        {/* Bottom row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 9, opacity: 0.6, letterSpacing: '0.1em', marginBottom: 2 }}>CÓDIGO</div>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.2em', fontFamily: 'monospace' }}>
              {card?.code || 'GC-XXXXXXXX'}
            </div>
            {design.showExpiry && (
              <div style={{ fontSize: 9, opacity: 0.5, marginTop: 4 }}>Válida 12 meses desde la fecha de compra</div>
            )}
          </div>
          {design.showQR && (
            <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: 6 }}>
              <QrCode style={{ width: 36, height: 36, opacity: 0.9 }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateCardModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({ amount: 50, recipient_name: '', recipient_email: '', sender_name: '', message: '', custom_amount: false });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/gift-cards', form);
      toast.success('Tarjeta regalo creada');
      onSave();
      onClose();
      setForm({ amount: 50, recipient_name: '', recipient_email: '', sender_name: '', message: '', custom_amount: false });
    } catch { toast.error('Error al crear'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="h-4 w-4 text-orange-500" /> Nueva tarjeta regalo</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Valor</Label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {PRESET_AMOUNTS.map(a => (
                <button key={a} onClick={() => setForm(f => ({ ...f, amount: a, custom_amount: false }))}
                  className={cn("py-2 rounded-xl border text-sm font-bold transition-all",
                    form.amount === a && !form.custom_amount ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 hover:border-slate-300 text-slate-700")}>
                  €{a}
                </button>
              ))}
            </div>
            <button onClick={() => setForm(f => ({ ...f, custom_amount: !f.custom_amount }))}
              className="text-xs text-orange-600 font-medium hover:underline">
              {form.custom_amount ? '← Usar valor predefinido' : '+ Valor personalizado'}
            </button>
            {form.custom_amount && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-slate-500 font-semibold">€</span>
                <Input type="number" min={5} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: +e.target.value }))} className="text-sm" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Nombre del destinatario</Label>
              <Input value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} placeholder="Para..." className="text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Email del destinatario</Label>
              <Input type="email" value={form.recipient_email} onChange={e => setForm(f => ({ ...f, recipient_email: e.target.value }))} placeholder="email@..." className="text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">De parte de</Label>
            <Input value={form.sender_name} onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))} placeholder="Nombre del remitente" className="text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Mensaje personalizado</Label>
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="¡Que lo disfrutes! 🎉" rows={3}
              className="w-full px-3 py-2 text-sm border border-input rounded-xl resize-none outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Gift className="h-4 w-4 mr-1" />} Crear tarjeta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GiftCards() {
  const [cards, setCards] = useState([]);
  const [design, setDesign] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('cards');
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, dR, rR] = await Promise.all([api.get('/gift-cards'), api.get('/gift-cards/design'), api.get('/restaurant')]);
      setCards(cR.data || []);
      setDesign(dR.data);
      setRestaurant(rR.data);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveDesign = async () => {
    setSaving(true);
    try { await api.patch('/gift-cards/design', design); toast.success('Diseño guardado'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const deleteCard = async (id) => {
    if (!window.confirm('¿Eliminar esta tarjeta?')) return;
    try { await api.delete(`/gift-cards/${id}`); setCards(prev => prev.filter(c => c.id !== id)); toast.success('Eliminada'); }
    catch { toast.error('Error'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Código copiado');
  };

  const totalValue = cards.reduce((s, c) => s + (c.amount || 0), 0);
  const totalRemaining = cards.reduce((s, c) => s + (c.balance_remaining || 0), 0);
  const activeCards = cards.filter(c => c.status === 'active').length;

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Tarjetas Regalo" helpSection="gift-cards" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Tarjetas Regalo" helpSection="gift-cards" actions={
        <div className="flex gap-2">
          {tab === 'design' && (
            <Button onClick={saveDesign} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar diseño
            </Button>
          )}
          {tab === 'cards' && (
            <Button onClick={() => setShowCreate(true)} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
              <Plus className="h-3.5 w-3.5 mr-1" /> Nueva tarjeta
            </Button>
          )}
        </div>
      } />

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#F97316,#EF4444)' }}>
            <Gift className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tarjetas Regalo</h1>
            <p className="text-sm text-slate-500">Crea, diseña y gestiona tarjetas regalo para tus clientes</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { l: 'Tarjetas activas', v: activeCards, icon: CreditCard, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Valor total emitido', v: `€${totalValue}`, icon: TrendingUp, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Saldo pendiente', v: `€${totalRemaining}`, icon: Gift, c: 'text-orange-600', bg: 'bg-orange-50' },
            { l: 'Tarjetas emitidas', v: cards.length, icon: Users, c: 'text-purple-600', bg: 'bg-purple-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4 w-4", s.c)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ k: 'cards', l: 'Tarjetas', icon: CreditCard }, { k: 'design', l: 'Diseño', icon: Palette }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={cn("flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              <t.icon className="h-3.5 w-3.5" />{t.l}
            </button>
          ))}
        </div>

        {/* CARDS TAB */}
        {tab === 'cards' && (
          <>
            {cards.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16 text-center">
                  <Gift className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                  <p className="text-slate-500 font-medium">No hay tarjetas regalo</p>
                  <p className="text-sm text-slate-400 mt-1 mb-4">Crea tu primera tarjeta regalo para empezar</p>
                  <Button onClick={() => setShowCreate(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Plus className="h-4 w-4 mr-1" /> Crear primera tarjeta
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {cards.map(card => (
                  <Card key={card.id} className="border-0 shadow-sm overflow-hidden group">
                    <div className="p-4 pb-0">
                      {design && <GiftCardVisual design={design} restaurant={restaurant} card={card} />}
                    </div>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{card.recipient_name || 'Sin destinatario'}</p>
                          <p className="text-xs text-slate-400">{card.recipient_email || '—'}</p>
                        </div>
                        <div className="text-right">
                          <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold",
                            card.status === 'active' ? 'bg-green-100 text-green-700' :
                            card.status === 'used' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700')}>
                            {card.status === 'active' ? 'Activa' : card.status === 'used' ? 'Usada' : 'Expirada'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Saldo restante:</span>
                        <span className="font-bold text-slate-800">€{card.balance_remaining}</span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => copyCode(card.code)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50">
                          {copied === card.code ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                          {copied === card.code ? 'Copiado' : card.code}
                        </button>
                        <button onClick={() => deleteCard(card.id)}
                          className="p-1.5 border border-red-200 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* DESIGN TAB */}
        {tab === 'design' && design && (
          <div className="grid grid-cols-5 gap-6">
            {/* Controls */}
            <div className="col-span-2 space-y-5">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Colores</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { k: 'primaryColor', l: 'Color principal' },
                    { k: 'secondaryColor', l: 'Color secundario' },
                    { k: 'textColor', l: 'Color del texto' },
                  ].map(f => (
                    <div key={f.k} className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600">{f.l}</Label>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded border border-slate-200" style={{ backgroundColor: design[f.k] }} />
                        <input type="color" value={design[f.k]} onChange={e => setDesign(d => ({ ...d, [f.k]: e.target.value }))}
                          className="w-8 h-7 rounded border border-slate-200 cursor-pointer p-0.5" />
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <Label className="text-xs text-slate-600">Usar gradiente</Label>
                    <Switch checked={!!design.gradient} onCheckedChange={v => setDesign(d => ({ ...d, gradient: v }))} />
                  </div>
                  {design.gradient && (
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Dirección del gradiente</Label>
                      <Select value={design.gradientDir || '135deg'} onValueChange={v => setDesign(d => ({ ...d, gradientDir: v }))}>
                        <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="135deg">Diagonal</SelectItem>
                          <SelectItem value="90deg">Vertical</SelectItem>
                          <SelectItem value="0deg">Horizontal</SelectItem>
                          <SelectItem value="45deg">Diagonal 45°</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Tipografía y forma</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Fuente</Label>
                    <Select value={design.fontFamily} onValueChange={v => setDesign(d => ({ ...d, fontFamily: v }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{FONT_OPTIONS.map(f => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Radio de bordes: {design.borderRadius}px</Label>
                    <input type="range" min={0} max={32} value={design.borderRadius}
                      onChange={e => setDesign(d => ({ ...d, borderRadius: +e.target.value }))}
                      className="w-full accent-orange-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Contenido</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Tagline</Label>
                    <Input value={design.tagline || ''} onChange={e => setDesign(d => ({ ...d, tagline: e.target.value }))}
                      placeholder="El regalo perfecto..." className="text-xs" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600">Mostrar QR</Label>
                      <Switch checked={!!design.showQR} onCheckedChange={v => setDesign(d => ({ ...d, showQR: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600">Mostrar expiración</Label>
                      <Switch checked={!!design.showExpiry} onCheckedChange={v => setDesign(d => ({ ...d, showExpiry: v }))} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Preview */}
            <div className="col-span-3">
              <Card className="border-0 shadow-sm sticky top-4">
                <CardHeader className="pb-3"><CardTitle className="text-sm">Vista previa</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <GiftCardVisual design={design} restaurant={restaurant} card={{ amount: 75, code: 'GC-PREVIEW1', status: 'active' }} />
                  <GiftCardVisual design={design} restaurant={restaurant} card={{ amount: 150, code: 'GC-PREVIEW2', status: 'active' }} />
                  <p className="text-xs text-slate-400 text-center">Vista previa con valores de ejemplo</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      <CreateCardModal open={showCreate} onClose={() => setShowCreate(false)} onSave={fetchAll} />
    </div>
  );
}
